<template>
  <div>
    <div class="main-content fe-account-page">
      <div class="container py-4 py-lg-5">
        <header class="fe-account-page__header">
          <h1 class="fe-account-page__title">Addresses</h1>
          <p class="fe-account-page__subtitle">
            Add, edit, or remove delivery and billing addresses for your orders.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div class="col-lg-9 col-md-8">
            <section class="fe-account-card fe-addr-toolbar">
              <div class="fe-account-card__head">
                <h2 class="fe-account-card__title">Saved addresses</h2>
                <button
                  v-if="!showForm"
                  type="button"
                  class="fe-addr-add-btn"
                  @click="openNewAddress"
                >
                  <i class="fas fa-plus"></i> Add address
                </button>
              </div>

              <div v-if="loading" class="fe-addr-loading">Loading addresses…</div>

              <div v-else-if="!showForm && addresses.length === 0" class="fe-addr-empty">
                <p>You don’t have any saved addresses yet.</p>
                <button type="button" class="fe-addr-add-btn" @click="openNewAddress">
                  <i class="fas fa-plus"></i> Add your first address
                </button>
              </div>

              <div v-if="showForm" class="fe-addr-form-card">
                <h3 class="fe-addr-form-title">{{ editingId ? 'Edit address' : 'New address' }}</h3>
                <form class="fe-addr-form" @submit.prevent="submitAddress">
                  <div class="row g-3">
                    <div class="col-12">
                      <label class="fe-addr-label">Full name <span class="req">*</span></label>
                      <input v-model="form.name" type="text" class="fe-addr-input" required />
                    </div>
                    <div class="col-12">
                      <label class="fe-addr-label">Phone</label>
                      <input v-model="form.phone" type="text" class="fe-addr-input" />
                    </div>
                    <div class="col-12">
                      <label class="fe-addr-label">Country <span class="req">*</span></label>
                      <select v-model.number="form.country_id" class="fe-addr-input fe-addr-select" required>
                        <option :value="0" disabled>Select country</option>
                        <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
                      </select>
                    </div>
                    <div class="col-12">
                      <label class="fe-addr-label">Street address <span class="req">*</span></label>
                      <input v-model="form.address" type="text" class="fe-addr-input" required />
                    </div>
                    <div class="col-12">
                      <label class="fe-addr-label">Apartment, suite (optional)</label>
                      <input v-model="form.address_2" type="text" class="fe-addr-input" />
                    </div>
                    <div class="col-md-6">
                      <label class="fe-addr-label">City <span class="req">*</span></label>
                      <input v-model="form.city" type="text" class="fe-addr-input" required />
                    </div>
                    <div class="col-md-6">
                      <label class="fe-addr-label">Postcode <span class="req">*</span></label>
                      <input v-model="form.zip_code" type="text" class="fe-addr-input" required />
                    </div>
                  </div>
                  <p v-if="formError" class="fe-addr-err">{{ formError }}</p>
                  <div class="fe-addr-form-actions">
                    <button type="submit" class="fe-addr-save" :disabled="saving">
                      {{ saving ? 'Saving…' : 'Save address' }}
                    </button>
                    <button type="button" class="fe-addr-cancel" @click="closeForm">Cancel</button>
                  </div>
                </form>
              </div>

              <ul v-if="!loading && addresses.length > 0 && !showForm" class="fe-addr-list">
                <li v-for="a in addresses" :key="a.id" class="fe-addr-item">
                  <div class="fe-addr-item__body">
                    <strong class="fe-addr-item__name">{{ displayName(a) }}</strong>
                    <span class="fe-addr-item__line">{{ lineAddress(a) }}</span>
                    <span v-if="a.phone" class="fe-addr-item__meta"><i class="fas fa-phone"></i> {{ a.phone }}</span>
                  </div>
                  <div class="fe-addr-item__actions">
                    <button type="button" class="fe-addr-icon-btn" title="Edit" @click="openEdit(a)">
                      <i class="fas fa-pen"></i>
                    </button>
                    <button type="button" class="fe-addr-icon-btn danger" title="Remove" @click="confirmRemove(a)">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </li>
              </ul>
            </section>

            <section class="fe-account-card mt-4">
              <div class="fe-account-card__head">
                <h2 class="fe-account-card__title">Account email</h2>
                <span class="fe-account-card__tag">Login</span>
              </div>
              <p class="fe-addr-note">
                To change your name or email, go to
                <router-link to="/my-account">My account</router-link>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>

    <cta-area></cta-area>
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'
import { toast } from 'vue3-toastify'
import { getAuthToken } from '@/services/axios'

const emptyForm = () => ({
  name: '',
  phone: '',
  country_id: 0,
  address: '',
  address_2: '',
  city: '',
  zip_code: '',
})

export default {
  name: 'MyAddressPage',
  components: {
    CtaArea,
    AccountSidebar,
  },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user')) || { name: 'Guest', email: '' },
      addresses: [],
      countries: [],
      loading: true,
      showForm: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
    }
  },
  mounted() {
    this.loadCountries()
    this.loadAddresses()
  },
  methods: {
    displayName(a) {
      return a.name || this.user.name || 'Address'
    },
    lineAddress(a) {
      const parts = [
        a.address || a.address_line_1,
        a.address_2 || a.address_line_2,
        a.city,
        a.zip_code || a.postal_code,
        this.countryName(a.country_id),
      ].filter(Boolean)
      return parts.join(', ')
    },
    countryName(id) {
      if (id == null) return ''
      const c = this.countries.find((x) => Number(x.id) === Number(id))
      return c ? c.name : ''
    },
    normalizeRow(row) {
      return {
        ...row,
        address: row.address || row.address_line_1 || '',
        address_2: row.address_2 || row.address_line_2 || '',
        zip_code: row.zip_code || row.postal_code || '',
      }
    },
    async loadCountries() {
      try {
        const { data } = await this.$axios.get('/countries')
        this.countries = Array.isArray(data) ? data : []
      } catch {
        this.countries = []
      }
    },
    async loadAddresses() {
      if (!this.user || !this.user.id) {
        this.loading = false
        return
      }
      this.loading = true
      try {
        const { data } = await this.$axios.get(`/get-user-address/${this.user.id}`)
        this.addresses = (Array.isArray(data) ? data : []).map((r) => this.normalizeRow(r))
      } catch {
        this.addresses = []
        toast.error('Could not load addresses.')
      } finally {
        this.loading = false
      }
    },
    openNewAddress() {
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showForm = true
    },
    openEdit(a) {
      const r = this.normalizeRow(a)
      this.editingId = r.id
      this.form = {
        name: r.name || '',
        phone: r.phone || '',
        country_id: r.country_id ? Number(r.country_id) : 0,
        address: r.address || '',
        address_2: r.address_2 || '',
        city: r.city || '',
        zip_code: r.zip_code || '',
      }
      this.formError = ''
      this.showForm = true
    },
    closeForm() {
      this.showForm = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
    },
    async submitAddress() {
      this.formError = ''
      if (!this.user || !this.user.id) {
        this.formError = 'You must be logged in.'
        return
      }
      const authToken = getAuthToken()
      if (!authToken) {
        this.formError = 'Your session has expired. Please log in again to save addresses.'
        toast.error(this.formError)
        return
      }
      if (!this.form.country_id) {
        this.formError = 'Please select a country.'
        return
      }
      this.saving = true
      const payload = {
        name: this.form.name.trim(),
        phone: this.form.phone?.trim() || null,
        country_id: Number(this.form.country_id),
        address: this.form.address.trim(),
        address_2: this.form.address_2?.trim() || null,
        city: this.form.city.trim(),
        zip_code: this.form.zip_code.trim(),
        auth_token: authToken,
      }
      try {
        if (this.editingId) {
          await this.$axios.put(`/web-user-address/${this.editingId}`, payload)
          toast.success('Address updated')
        } else {
          await this.$axios.post('/web-user-address', payload)
          toast.success('Address saved')
        }
        this.closeForm()
        await this.loadAddresses()
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          (e.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' ')) ||
          'Could not save address.'
        this.formError = msg
        toast.error(msg)
      } finally {
        this.saving = false
      }
    },
    confirmRemove(a) {
      if (!confirm('Remove this address?')) return
      this.removeAddress(a.id)
    },
    async removeAddress(id) {
      const authToken = getAuthToken()
      if (!authToken) {
        toast.error('Your session has expired. Please log in again.')
        return
      }
      try {
        await this.$axios.delete(`/web-user-address/${id}`, {
          params: { auth_token: authToken },
        })
        toast.success('Address removed')
        await this.loadAddresses()
      } catch (e) {
        toast.error(e.response?.data?.message || 'Could not remove address.')
      }
    },
  },
}
</script>

<style scoped>
.fe-account-page {
  background: linear-gradient(180deg, #f8f8f8 0%, #fff 120px);
}

.fe-account-page__header {
  text-align: center;
  margin-bottom: 2rem;
}

.fe-account-page__title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #141414;
  margin: 0 0 0.5rem;
}

.fe-account-page__subtitle {
  margin: 0 auto;
  max-width: 520px;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
}

.fe-account-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ececec;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
  padding: 1.35rem 1.5rem 1.5rem;
}

.fe-account-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #eee;
}

.fe-account-card__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #141414;
}

.fe-account-card__tag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 16%, #fff);
  color: #5c4a00;
}

.fe-addr-toolbar .fe-account-card__head {
  margin-bottom: 0.75rem;
}

.fe-addr-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--fe-accent, #fcbe00) 45%, #ddd);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 12%, #fff) 0%,
    #fff 100%
  );
  font-weight: 800;
  font-size: 0.85rem;
  color: #111;
  cursor: pointer;
}

.fe-addr-add-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
}

.fe-addr-loading,
.fe-addr-empty {
  padding: 1rem 0;
  color: #64748b;
  font-size: 0.95rem;
}

.fe-addr-empty p {
  margin: 0 0 1rem;
}

.fe-addr-form-card {
  margin-top: 0.5rem;
  padding: 1.25rem;
  border-radius: 14px;
  border: 1px solid #e8ecf1;
  background: #fafbfc;
}

.fe-addr-form-title {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
}

.fe-addr-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.req {
  color: #dc2626;
}

.fe-addr-input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.9rem;
  font-size: 0.95rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}

.fe-addr-input:focus {
  outline: none;
  border-color: var(--fe-accent, #fcbe00);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--fe-accent, #fcbe00) 20%, transparent);
}

.fe-addr-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12'%3E%3Cpath fill='%2364748b' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}

.fe-addr-err {
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0.75rem 0 0;
}

.fe-addr-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.15rem;
}

.fe-addr-save {
  padding: 0.65rem 1.35rem;
  border: none;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  background: linear-gradient(
    135deg,
    var(--fe-accent, #fcbe00) 0%,
    color-mix(in srgb, var(--fe-accent-hover, #e5ac00) 90%, #111) 100%
  );
  color: #1a1a1a;
}

.fe-addr-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.fe-addr-cancel {
  padding: 0.65rem 1.1rem;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
  cursor: pointer;
  color: #475569;
}

.fe-addr-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.fe-addr-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.fe-addr-item:last-child {
  border-bottom: none;
}

.fe-addr-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.fe-addr-item__name {
  font-size: 1rem;
  color: #0f172a;
}

.fe-addr-item__line {
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.45;
  word-break: break-word;
}

.fe-addr-item__meta {
  font-size: 0.82rem;
  color: #475569;
}

.fe-addr-item__meta i {
  margin-right: 0.35rem;
  opacity: 0.8;
}

.fe-addr-item__actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.fe-addr-icon-btn {
  width: 38px;
  height: 38px;
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.fe-addr-icon-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
  color: #111;
}

.fe-addr-icon-btn.danger:hover {
  border-color: #fecaca;
  background: #fff5f5;
  color: #b91c1c;
}

.fe-addr-note {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
}

.fe-addr-note a {
  color: var(--fe-accent, #b8860b);
  font-weight: 700;
}
</style>